import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Video, Calendar, ExternalLink, Link2, AlertTriangle, Clock, Filter, MoreVertical } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, subDays } from "date-fns";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { OrphanRecordingResolver } from "@/components/admin/OrphanRecordingResolver";
import { useRole } from "@/contexts/RoleContext";

interface RecordingRow {
  id: string;
  closer_id: string;
  client_id: string | null;
  demo_id: string | null;
  fathom_recording_id: string;
  fathom_share_url: string | null;
  title: string | null;
  duration_seconds: number | null;
  recorded_at: string;
  transcript_summary: string | null;
  closers: { name: string } | null;
  clients: { company_name: string } | null;
  demos: { prospect_name: string; demo_date: string } | null;
}

function formatDuration(seconds: number | null): string {
  if (!seconds || seconds <= 0) return "--:--";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

export default function AdminDemoLibrary() {
  const queryClient = useQueryClient();
  const { adminSubRole } = useRole();
  const canReassign = adminSubRole === "super_admin" || adminSubRole === "team_admin";

  // Filter state
  const [closerFilter, setCloserFilter] = useState<string>("all");
  const [clientFilter, setClientFilter] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<string>(format(subDays(new Date(), 30), "yyyy-MM-dd"));
  const [dateTo, setDateTo] = useState<string>(format(new Date(), "yyyy-MM-dd"));
  const [orphansOnly, setOrphansOnly] = useState(false);

  // Fetch closers for filter dropdown
  const { data: closers } = useQuery({
    queryKey: ["closers-filter"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("closers")
        .select("id, name")
        .eq("status", "active")
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
  });

  // Fetch clients for filter dropdown
  const { data: clients } = useQuery({
    queryKey: ["clients-filter"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("id, company_name")
        .order("company_name");
      if (error) throw error;
      return data ?? [];
    },
  });

  // Fetch recordings with joins
  const { data: recordings, isLoading } = useQuery({
    queryKey: ["demo-recordings", closerFilter, clientFilter, dateFrom, dateTo, orphansOnly],
    queryFn: async () => {
      let query = supabase
        .from("demo_recordings")
        .select("*, closers(name), clients(company_name), demos(prospect_name, demo_date)")
        .order("recorded_at", { ascending: false });

      if (closerFilter && closerFilter !== "all") query = query.eq("closer_id", closerFilter);
      if (clientFilter && clientFilter !== "all") query = query.eq("client_id", clientFilter);
      if (dateFrom) query = query.gte("recorded_at", dateFrom);
      if (dateTo) query = query.lte("recorded_at", dateTo + "T23:59:59Z");
      if (orphansOnly) query = query.is("demo_id", null);

      const { data, error } = await query.limit(100);
      if (error) {
        toast.error("Failed to load recordings");
        throw error;
      }
      return (data ?? []) as RecordingRow[];
    },
  });

  // Stats
  const stats = useMemo(() => {
    if (!recordings) return { total: 0, linked: 0, orphans: 0, totalHours: 0 };
    const linked = recordings.filter((r) => r.demo_id !== null).length;
    const orphans = recordings.filter((r) => r.demo_id === null).length;
    const totalSeconds = recordings.reduce((sum, r) => sum + (r.duration_seconds ?? 0), 0);
    return {
      total: recordings.length,
      linked,
      orphans,
      totalHours: Math.round((totalSeconds / 3600) * 10) / 10,
    };
  }, [recordings]);

  const handleWatch = (shareUrl: string | null) => {
    if (shareUrl) {
      window.open(shareUrl, "_blank", "noopener");
    } else {
      toast.info("No share URL available for this recording");
    }
  };

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Video className="w-6 h-6 text-secondary" />
            <h1 className="text-2xl md:text-3xl font-bold">Demo Library</h1>
          </div>
          <p className="text-muted-foreground mt-1">All demo recordings synced from Fathom</p>
        </div>
        <Button
          variant={orphansOnly ? "default" : "outline"}
          size="sm"
          onClick={() => setOrphansOnly(!orphansOnly)}
          className="gap-2"
        >
          <AlertTriangle className="w-4 h-4" />
          {orphansOnly ? "Show All" : "Show Orphans Only"}
          {stats.orphans > 0 && !orphansOnly && (
            <BadgeVenture variant="warning" className="ml-1 text-xs">
              {stats.orphans}
            </BadgeVenture>
          )}
        </Button>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-4">
        <Select value={closerFilter} onValueChange={setCloserFilter}>
          <SelectTrigger className="w-[200px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Closers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Closers</SelectItem>
            {closers?.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Clients" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Clients</SelectItem>
            {clients?.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.company_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-2 border border-border rounded-md bg-background text-sm"
          />
          <span className="text-muted-foreground">to</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-2 border border-border rounded-md bg-background text-sm"
          />
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <VentureCard>
          <VentureCardContent className="text-center py-4">
            <p className="text-3xl font-bold">{stats.total}</p>
            <p className="text-sm text-muted-foreground">Total Recordings</p>
          </VentureCardContent>
        </VentureCard>
        <VentureCard>
          <VentureCardContent className="text-center py-4">
            <p className="text-3xl font-bold text-secondary">{stats.linked}</p>
            <p className="text-sm text-muted-foreground">Linked to Demos</p>
          </VentureCardContent>
        </VentureCard>
        <VentureCard>
          <VentureCardContent className="text-center py-4">
            <p className={`text-3xl font-bold ${stats.orphans > 0 ? "text-warning" : ""}`}>
              {stats.orphans}
            </p>
            <p className="text-sm text-muted-foreground">Orphan Recordings</p>
          </VentureCardContent>
        </VentureCard>
        <VentureCard>
          <VentureCardContent className="text-center py-4">
            <div className="flex items-center justify-center gap-1">
              <Clock className="w-5 h-5 text-muted-foreground" />
              <p className="text-3xl font-bold">{stats.totalHours}</p>
            </div>
            <p className="text-sm text-muted-foreground">Hours Recorded</p>
          </VentureCardContent>
        </VentureCard>
      </div>

      {/* Recordings table */}
      <VentureCard>
        <VentureCardHeader>
          <VentureCardTitle>Recordings ({recordings?.length ?? 0})</VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-12 rounded-lg bg-muted animate-pulse" />
              ))}
            </div>
          ) : !recordings || recordings.length === 0 ? (
            <div className="text-center py-12">
              <Video className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No demo recordings yet</h3>
              <p className="text-muted-foreground">
                Connect Fathom API keys for your closers in Sales Operations.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Title</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Closer</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Client</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Demo</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Recorded</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Duration</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recordings.map((rec) => (
                    <tr key={rec.id} className="border-b border-border hover:bg-surface transition-colors">
                      <td className="py-3 px-4">
                        <span className="font-medium">{rec.title || "Untitled Recording"}</span>
                      </td>
                      <td className="py-3 px-4">
                        {rec.closers?.name ?? <span className="text-muted-foreground">Unknown</span>}
                      </td>
                      <td className="py-3 px-4">
                        {rec.clients?.company_name ?? (
                          <span className="text-muted-foreground">Unassigned</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {rec.demo_id && rec.demos ? (
                          <BadgeVenture variant="success">
                            {rec.demos.prospect_name}
                          </BadgeVenture>
                        ) : (
                          <OrphanRecordingResolver
                            recording={{
                              id: rec.id,
                              closer_id: rec.closer_id,
                              recorded_at: rec.recorded_at,
                              title: rec.title,
                            }}
                            onResolved={() =>
                              queryClient.invalidateQueries({ queryKey: ["demo-recordings"] })
                            }
                          />
                        )}
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {format(new Date(rec.recorded_at), "MMM d, yyyy HH:mm")}
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {formatDuration(rec.duration_seconds)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Watch Recording"
                            onClick={() => handleWatch(rec.fathom_share_url)}
                            disabled={!rec.fathom_share_url}
                          >
                            <Video className="w-4 h-4" />
                          </Button>
                          {rec.fathom_share_url && (
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Open in Fathom"
                              onClick={() => window.open(rec.fathom_share_url!, "_blank", "noopener")}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          )}
                          {canReassign && rec.demo_id && (
                            <RowKebab rec={rec} clients={clients ?? []} />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </VentureCardContent>
      </VentureCard>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// RowKebab: admin-only per-row kebab menu (Phase 16.8-03)
// ---------------------------------------------------------------------------

interface RowKebabProps {
  rec: RecordingRow;
  clients: Array<{ id: string; company_name: string }>;
}

type PickerMode = null | "client" | "demo" | "unlink-confirm";

function RowKebab({ rec, clients }: RowKebabProps) {
  const queryClient = useQueryClient();
  const [picker, setPicker] = useState<PickerMode>(null);
  const [clientFilter, setClientFilter] = useState("");
  const [busy, setBusy] = useState(false);

  const filteredClients = useMemo(() => {
    const q = clientFilter.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => (c.company_name ?? "").toLowerCase().includes(q));
  }, [clients, clientFilter]);

  const { data: demosForClient } = useQuery({
    queryKey: ["demos-for-client", rec.client_id],
    queryFn: async () => {
      if (!rec.client_id) return [];
      const { data, error } = await supabase
        .from("demos")
        .select("id, demo_date, status, client_id")
        .eq("client_id", rec.client_id)
        .order("demo_date", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data ?? [];
    },
    enabled: picker === "demo" && !!rec.client_id,
  });

  // Each handler invokes resolve-recording directly (one invoke per action type)
  // so static analysis (grep of call sites) reflects the 3 mutation paths.

  const handleReassignClient = async (newClientId: string) => {
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke<{
        success: boolean;
        cleared_demo_id?: boolean;
      }>('resolve-recording', {
        body: { recording_id: rec.id, action: 'reassign', client_id: newClientId },
      });
      if (error) throw error;
      const newClient = clients.find((c) => c.id === newClientId);
      if (data?.cleared_demo_id) {
        toast.info(
          `Recording reassigned to ${newClient?.company_name ?? 'new client'}. Previous demo link cleared.`,
        );
      } else {
        toast.success('Recording reassigned');
      }
      queryClient.invalidateQueries({ queryKey: ['demo-recordings'] });
      setPicker(null);
    } catch {
      toast.error('Failed to reassign recording');
    } finally {
      setBusy(false);
    }
  };

  const handleReassignDemo = async (newDemoId: string) => {
    setBusy(true);
    try {
      const { error } = await supabase.functions.invoke<{
        success: boolean;
      }>('resolve-recording', {
        body: { recording_id: rec.id, action: 'reassign', demo_id: newDemoId },
      });
      if (error) throw error;
      toast.success('Recording re-linked to demo');
      queryClient.invalidateQueries({ queryKey: ['demo-recordings'] });
      setPicker(null);
    } catch {
      toast.error('Failed to reassign recording');
    } finally {
      setBusy(false);
    }
  };

  const handleUnlink = async () => {
    setBusy(true);
    try {
      const { error } = await supabase.functions.invoke<{
        success: boolean;
      }>('resolve-recording', {
        body: { recording_id: rec.id, action: 'unlink' },
      });
      if (error) throw error;
      toast.success('Recording unlinked');
      queryClient.invalidateQueries({ queryKey: ['demo-recordings'] });
      setPicker(null);
    } catch {
      toast.error('Failed to unlink recording');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={picker !== null} onOpenChange={(open) => !open && setPicker(null)}>
      <PopoverTrigger asChild>
        {/* Invisible anchor so the Popover positions under the kebab button */}
        <span className="sr-only" aria-hidden />
      </PopoverTrigger>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" title="More actions" disabled={busy}>
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setClientFilter("");
              setPicker("client");
            }}
          >
            Reassign client…
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={!rec.client_id}
            title={!rec.client_id ? "Set client first" : undefined}
            onSelect={(e) => {
              e.preventDefault();
              if (!rec.client_id) return;
              setPicker("demo");
            }}
          >
            Reassign demo…
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setPicker("unlink-confirm");
            }}
          >
            Unlink
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <PopoverContent align="end" className="w-80 p-3 space-y-2">
        {picker === "client" && (
          <div className="space-y-2">
            <Input
              autoFocus
              placeholder="Search clients…"
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
            />
            <div className="max-h-64 overflow-auto flex flex-col gap-1">
              {filteredClients.length === 0 ? (
                <p className="text-sm text-muted-foreground px-2 py-1">No clients found.</p>
              ) : (
                filteredClients.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    disabled={busy}
                    onClick={() => handleReassignClient(c.id)}
                    className="text-left px-2 py-1.5 rounded hover:bg-accent text-sm"
                  >
                    {c.company_name}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
        {picker === "demo" && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground px-1">
              Pick a demo for the current client.
            </p>
            <div className="max-h-64 overflow-auto flex flex-col gap-1">
              {!rec.client_id ? (
                <button
                  type="button"
                  disabled
                  title="Set client first"
                  className="text-left px-2 py-1.5 rounded text-sm opacity-50"
                >
                  Set client first
                </button>
              ) : !demosForClient || demosForClient.length === 0 ? (
                <p className="text-sm text-muted-foreground px-2 py-1">No demos found.</p>
              ) : (
                demosForClient.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    disabled={busy}
                    onClick={() => handleReassignDemo(d.id)}
                    className="text-left px-2 py-1.5 rounded hover:bg-accent text-sm"
                  >
                    {format(new Date(d.demo_date), "MMM d, yyyy")} — {d.status}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
        {picker === "unlink-confirm" && (
          <div className="space-y-2">
            <p className="text-sm">Unlink this recording?</p>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="danger" disabled={busy} onClick={handleUnlink}>
                Yes, unlink
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setPicker(null)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
